import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth";
import { ref, update, get, set } from "firebase/database";
import { useNavigate, Link } from "react-router-dom";
import { 
  FaLock, FaEnvelope, FaKey, FaEye, FaEyeSlash, 
  FaMoon, FaSun, FaCommentDots, FaMobileAlt, FaBolt,
  FaShieldAlt
} from "react-icons/fa";
import "./Auth.css";

function Login() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: localStorage.getItem("remember_email") || "",
    password: "",
    rememberMe: localStorage.getItem("remember_me") === "true"
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // Theme Management (Light & Dark)
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    if (!resetEmail) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail, {
        url: window.location.origin + "/login",
        handleCodeInApp: false
      });
      setInfoMessage("Password reset email sent! Check your inbox.");
      setTimeout(() => setIsResetMode(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loginUser = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");

    if (!formData.email || !formData.password) {
      setError("All fields are required.");
      return;
    }

    try {
      setLoading(true);

      if (formData.rememberMe) {
        localStorage.setItem("remember_me", "true");
        localStorage.setItem("remember_email", formData.email);
      } else {
        localStorage.removeItem("remember_me");
        localStorage.removeItem("remember_email");
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;
      await user.reload();

      if (!user.emailVerified) {
        setError("Please verify your email before logging in.");
        return;
      }

      const userRef = ref(db, "users/" + user.uid);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        await update(userRef, {
          isEmailVerified: true,
          online: true,
          lastLogin: Date.now(),
        });
      } else {
        // Create user record if missing
        await set(userRef, {
          uid: user.uid,
          name: user.displayName || user.email.split("@")[0],
          email: user.email,
          photoURL: user.photoURL || "",
          bio: "Available on Bharat Connect",
          isEmailVerified: true,
          online: true,
          createdAt: Date.now()
        });
      }

      navigate("/chat");
    } catch (err) {
      console.error(err);
      switch (err.code) {
        case "auth/user-not-found":
          setError("User not found.");
          break;
        case "auth/wrong-password":
        case "auth/invalid-credential":
          setError("Incorrect password or credentials.");
          break;
        case "auth/invalid-email":
          setError("Invalid email format.");
          break;
        case "auth/too-many-requests":
          setError("Too many attempts. Try again later.");
          break;
        default:
          setError("Login failed: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="auth-page-container">
      {/* Top Navbar */}
      <header className="auth-navbar">
        <Link to="/" className="auth-nav-logo">
          <div className="auth-logo-icon">
            <FaShieldAlt />
          </div>
          <span>Bharat Connect</span>
        </Link>
        <button className="auth-theme-toggle" onClick={toggleTheme}>
          {theme === "dark" ? <FaSun style={{ color: '#eab308' }} /> : <FaMoon style={{ color: '#00a884' }} />}
          <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </header>

      {/* Main Split Layout (45% Branding / 55% Glass Card) */}
      <main className="auth-main-content">
        {/* 45% Branding Panel */}
        <section className="auth-brand-panel">
          <div>
            <div className="auth-brand-badge">
              <FaLock /> <span>Secure Messaging</span>
            </div>
            <h1 className="auth-brand-title">Bharat Connect</h1>
            <p className="auth-brand-subtitle">
              Secure • Real-time • Private Workspace Communication
            </p>
            
            <div className="auth-brand-features">
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaCommentDots />
                </div>
                <span>End-to-End Encryption</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaMobileAlt />
                </div>
                <span>Cross Platform Sync</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaBolt />
                </div>
                <span>Fast & Reliable High-Speed Messaging</span>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            © 2026 Bharat Connect. Enterprise-grade encrypted network.
          </div>
        </section>

        {/* 55% Glass Auth Card */}
        <section className="auth-form-card">
          <div className="auth-card-header">
            <h2>{isResetMode ? "Reset Password" : "Welcome Back 👋"}</h2>
            <p>
              {isResetMode
                ? "Enter your email to receive a password reset link."
                : "Continue to your secure workspace."}
            </p>
          </div>

          {error && <div className="auth-error-alert">{error}</div>}
          {infoMessage && <div className="auth-success-alert">{infoMessage}</div>}

          {isResetMode ? (
            <form onSubmit={handlePasswordReset}>
              <div className="auth-form-group">
                <label>Email Address</label>
                <div className="auth-input-wrapper">
                  <FaEnvelope className="auth-input-icon" />
                  <input
                    type="email"
                    className="auth-input"
                    placeholder="you@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? "Sending Link..." : "Send Reset Link"}
              </button>

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <span
                  className="auth-forgot-link"
                  onClick={() => {
                    setIsResetMode(false);
                    setError("");
                    setInfoMessage("");
                  }}
                >
                  Back to Login
                </span>
              </div>
            </form>
          ) : (
            <>
              <form onSubmit={loginUser}>
                <div className="auth-form-group">
                  <label>Email Address</label>
                  <div className="auth-input-wrapper">
                    <FaEnvelope className="auth-input-icon" />
                    <input
                      type="email"
                      name="email"
                      className="auth-input"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="auth-form-group">
                  <label>Password</label>
                  <div className="auth-input-wrapper">
                    <FaKey className="auth-input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="auth-input"
                      placeholder="••••••••••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <div
                      className="auth-eye-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                </div>

                <div className="auth-extras-row">
                  <label className="auth-checkbox-label">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <span>Remember Me</span>
                  </label>
                  <span
                    className="auth-forgot-link"
                    onClick={() => {
                      setIsResetMode(true);
                      setError("");
                      setInfoMessage("");
                      setResetEmail(formData.email);
                    }}
                  >
                    Forgot Password?
                  </span>
                </div>

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? "Logging in..." : "LOGIN"}
                </button>
              </form>

              <div className="auth-card-footer" style={{ marginTop: "24px" }}>
                <span>Don't have an account?</span>
                <Link to="/register">Register →</Link>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

export default Login;
